import { createSelector } from '@reduxjs/toolkit';
import set from 'lodash/fp/set';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';

import {
  CHANGE_MEAL_OPTION,
  UNDO_MEAL_OPTIONS,
  SET_EDITED_BASKET,
  CLEAR_EDITED_BASKET,
  UNDO_BASKET_EDITS,
  REMOVE_MEAL,
} from 'actions/basketEdits';
import {
  ADD_BASKET_RESPONSE,
  UPDATE_BASKET_RESPONSE,
  UNDONATE_BASKET_RESPONSE,
  DONATE_BASKET_RESPONSE,
} from 'actions/baskets';
import { EMPTY_ARRAY, EMPTY_OBJECT } from 'lib/constants';

export const setMealOptions = (mealOptionSelections, mealId) => {
  const selections = get(mealOptionSelections, mealId, EMPTY_ARRAY);

  if (selections.length === 0) return EMPTY_OBJECT;

  return selections.reduce(
    (hash, option) => ({
      ...hash,
      [option.mealOptionGroupId]: option.mealOptionId,
    }),
    {}
  );
};

export const updateBasket = ({ meals, mealOptionSelections }) => {
  if (!meals) return EMPTY_OBJECT;

  return meals.reduce((hash, meal) => {
    const options = setMealOptions(mealOptionSelections, meal.id);

    if (options === EMPTY_OBJECT) return hash;

    return {
      ...hash,
      [meal.id]: options,
    };
  }, EMPTY_OBJECT);
};

const initialState = {};

export default (state = initialState, action) => {
  switch (action.type) {
    case ADD_BASKET_RESPONSE:
    case UNDONATE_BASKET_RESPONSE:
    case UPDATE_BASKET_RESPONSE:
    case DONATE_BASKET_RESPONSE:
      return updateBasket(action.response.weeklyBasket);
    case UNDO_BASKET_EDITS:
    case SET_EDITED_BASKET:
      return updateBasket(action.basket);
    case CHANGE_MEAL_OPTION:
      return set([action.mealId, action.mealOptionGroupId], action.mealOptionId, state);
    case UNDO_MEAL_OPTIONS: {
      if (isEmpty(action.mealOptions)) {
        return state[action.mealId] ? omit(state, action.mealId) : state;
      }
      return set(action.mealId, action.mealOptions, state);
    }
    case CLEAR_EDITED_BASKET:
      return initialState;
    case REMOVE_MEAL:
      return omit(state, action.mealId);
    default:
      return state;
  }
};

// Selector Logic
const getMealOptionsState = state => state || EMPTY_OBJECT;
const getMealId = (state, mealId) => mealId;

export const selectMealOptionsForMeal = createSelector(
  [getMealOptionsState, getMealId],
  (mealOptions, mealId) => {
    return get(mealOptions, mealId, EMPTY_OBJECT);
  }
);
